"use client";
import Image from "next/image";
import Button from "./Button";
import Slider from "../../common/components/Slider";
import Obfuscate from "../../common/components/Obfuscate";
import { useCallback } from "react";
import * as matomo from "../../analytics/utils/matomo";
import { useTranslations } from "next-intl";
import Icon from "../../common/components/Icon";
import { Link } from "../../../navigation";

export function BannerSlider({ children }) {
  if (children?.length === 0) {
    return children;
  }
  return (
    <div className="banner-slider">
      <Slider navigation={true} autoPlay={false}>
        {children}
      </Slider>
    </div>
  );
}

export default function Banner({
  appearance,
  title,
  subtitle,
  employeeName,
  employeeTitle,
  employeePhotoPath,
  text,
  callToActionButton,
  forceColor,
  primaryColor,
}: {
  appearance: "primary" | "secondary";
  title: string;
  subtitle: string;
  employeeName: string;
  employeeTitle: string;
  employeePhotoPath: string;
  text: string;
  forceColor?: string;
  primaryColor?: string;
  callToActionButton?: {
    label: string;
    href: string;
  };
}) {
  const t = useTranslations();
  const clickEventTracker = useCallback((action) => {
    return () => {
      matomo.trackEvent("Contact Bar Click", action, document?.location?.href);
    };
  }, []);
  return (
    <div className="banner-wrapper" data-appearance={appearance}>
      <div className="overflow-hidden">
        <div className="banner container container-narrow position-relative px-3 py-5">
          <div className="z-1 position-relative d-flex flex-column gap-3">
            <div>
              <h2
                className="banner-title regular-serif my-0"
                style={{
                  color: forceColor && "#FFFFFF",
                }}
              >
                {title}{" "}
                <span
                  className="banner-subtitle"
                  style={{
                    color: forceColor,
                  }}
                >
                  {subtitle}
                </span>
              </h2>
            </div>
            <div className="banner__content__small">
              <p className="bold">{employeeName}</p>
              <p>{employeeTitle}</p>
            </div>
            <p className="banner__content__small mt-5">{text}</p>
            {callToActionButton ? (
              <div className="mt-4">
                <Button appearance="secondary" href={callToActionButton.href}>
                  {callToActionButton.label}
                </Button>
              </div>
            ) : (
              <div className="mt-3 d-flex align-items-baseline gap-3 flex-wrap">
                <Obfuscate
                  className="banner-bar__item"
                  onClick={clickEventTracker("call_us_button_click")}
                  href="tel:+41564841500"
                  aria-label={t("call_us")}
                  style={{
                    backgroundColor: forceColor,
                  }}
                >
                  <div className="text-white">
                    <span>
                      {t("call_us")}{" "}
                      <Obfuscate
                        element="span"
                        style={{
                          backgroundColor: forceColor,
                        }}
                      >
                        {t("contact_us_number")}
                      </Obfuscate>
                    </span>
                  </div>
                  <Icon icon="phone" />
                </Obfuscate>
                <Obfuscate
                  style={{
                    backgroundColor: forceColor,
                  }}
                  className="banner-bar__item"
                  href="mailto:info@publicare.ch"
                  onClick={clickEventTracker("email_button_click")}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={t("email_us")}
                >
                  <div className="text-white">
                    <span>{t("email_us")}</span>
                  </div>
                  <Icon icon="letter" fill="#FFFFFF" />
                </Obfuscate>
                <Link
                  style={{
                    backgroundColor: forceColor,
                  }}
                  type="button"
                  className="no-button banner-bar__item align-items-center"
                  onClick={() => {
                    clickEventTracker("callback_button_click")();
                  }}
                  href="call-back"
                  aria-label={t("callback_us")}
                >
                  <div className="text-white">
                    <span>{t("callback_us")}</span>
                  </div>
                  <Icon icon="phone-time" />
                </Link>
              </div>
            )}
          </div>
          <Image
            src={employeePhotoPath}
            alt=""
            quality={80}
            width={500}
            height={363}
            style={{ objectFit: "cover", objectPosition: "center" }}
            className="position-absolute banner__image"
            unoptimized={employeePhotoPath.endsWith(".svg")}
          />
        </div>
      </div>
    </div>
  );
}
